import { type ReadonlyURLSearchParams, type useParams } from "next/navigation";
import { getPathSegmentByIndex } from "@/utils/url/getPathSegmentByIndex";
import { removeRedundantSlashFromPath } from "@/utils/url/removeRedundantSlashFromPath";
import type {
  ExhibitorListFormType,
  ExhibitorListQueryParams,
  FilterTypeWithoutCategories,
  MultiFilterShape,
  Pagination,
} from "./types";
import type { BreadcrumbItem } from "@/exhibitors/ExhibitorProfile/ExBreadcrumbs";
import {
  DisplayType,
  DisplayView,
  ExhibitorPageType,
  EXHIBITORS_PATHNAME,
  FEATURED_PRODUCTS_PATHNAME,
  FILTER_VALUE_PREFIX,
  PRODUCTS_PATHNAME,
  RESOURCES_PATHNAME,
} from "./constants";

export function getPageType(
  params: ReturnType<typeof useParams>,
): ExhibitorPageType {
  const { slug = [] } = params;

  if (slug.includes(EXHIBITORS_PATHNAME)) {
    const pathname = slug[slug.length - 2];

    if (pathname === EXHIBITORS_PATHNAME) {
      if (slug[slug.length - 1] === FEATURED_PRODUCTS_PATHNAME) {
        return ExhibitorPageType.FeaturedListing;
      }

      return ExhibitorPageType.ExhibitorProfile;
    }

    if (pathname === PRODUCTS_PATHNAME) {
      return ExhibitorPageType.ProductPage;
    }

    if (pathname === RESOURCES_PATHNAME) {
      return ExhibitorPageType.ResourcePage;
    }
  }

  return ExhibitorPageType.Listing;
}

function getCurrentSlugFromPath(path: string) {
  const index = path.split("/").filter(Boolean).length - 1;
  return getPathSegmentByIndex(path, index);
}

export function getExhibitorSlugFromPath(path: string) {
  return getCurrentSlugFromPath(path);
}

export function getDeepExhibitorSlugFromPath(path: string) {
  const index = path.split("/").filter(Boolean).length - 3;
  return getPathSegmentByIndex(path, index);
}

export function getProductSlugFromPath(path: string) {
  return getCurrentSlugFromPath(path);
}

export function getResourceSlugFromPath(path: string) {
  return getCurrentSlugFromPath(path);
}

export function transformQueryParamsToFormValues(
  params: ReadonlyURLSearchParams,
  { limit = 50 }: Partial<ExhibitorListFormType>,
): ExhibitorListFormType {
  const multiFilters = params
    .getAll("filters")
    .map((filter) => filter.split(":"))
    .reduce((acc, current) => {
      const [filterType, values] = current;

      acc[filterType as FilterTypeWithoutCategories] = values.split(",").reduce(
        (valueAcc, fieldName) => {
          valueAcc[`${FILTER_VALUE_PREFIX}${fieldName}`] = true;
          return valueAcc;
        },
        {} as Record<string, boolean>,
      );

      return acc;
    }, {} as MultiFilterShape);

  return {
    page: getNumberValue(params.get("page"), 1),
    limit: getNumberValue(params.get("limit"), limit),
    searchTerm: params.get("q") || "",
    categories: params.get("category") || "",
    ...multiFilters,
  };
}

export function transformFormValuesToQueryParams(
  values: ExhibitorListFormType,
  browserQuery = false,
): ExhibitorListQueryParams {
  const {
    page,
    limit,
    searchTerm,
    categories: category,
    ...restValues
  } = values;

  const query: ExhibitorListQueryParams = {
    page,
    limit,
    q: searchTerm || undefined,
    filters: [],
  };

  if (category) {
    if (browserQuery) {
      query.category = category;
    } else {
      query.taxonomy = "categories";
      query.taxonomyID = category;
    }
  }

  Object.keys(restValues).forEach((key) => {
    const category = key as FilterTypeWithoutCategories;
    const values = Object.keys(restValues[category])
      .filter((name) => restValues[category][name])
      .map((name) => name.replace(FILTER_VALUE_PREFIX, ""));

    if (values.length > 0) {
      query.filters!.push(`${category}:${values.join()}`);
    }
  });

  return query;
}

export function getDefaultDisplayView(
  gridView: boolean,
  thumbnailView: boolean,
) {
  if (gridView) {
    return DisplayView.Grid;
  }

  if (thumbnailView) {
    return DisplayView.Thumbs;
  }

  return DisplayView.List;
}

export function getPaginationConfig(
  displayType: DisplayType,
  configs: Array<Pagination | undefined>,
) {
  switch (displayType) {
    case DisplayType.Exhibitors:
      return configs[0];

    case DisplayType.Products:
      return configs[1];

    case DisplayType.Resources:
      return configs[2];
  }
}

export function getPaginationItemsSize(pagination: Pagination | undefined) {
  return pagination ? +pagination.size : 0;
}

export function scrollToModuleTop() {
  document
    .getElementById("exhibitor-list")
    ?.scrollIntoView({ behavior: "smooth" });
}

export function getNumberValue(value: string | null, defaultValue: number) {
  return value ? +value : defaultValue;
}

function createURL(pagePath: string, subPagePath: string) {
  return removeRedundantSlashFromPath(`${pagePath}/${subPagePath}`);
}

export function getBreadcrumbsConfig(
  pathname: string,
  eventName: string,
  exhibitorName: string,
  pageName?: string,
) {
  const [pagePath, restPath] = pathname.split(EXHIBITORS_PATHNAME);
  const [exhibitorSlug] = restPath.slice(1).split("/");

  const breadcrumbs: BreadcrumbItem[] = [
    { path: pagePath, label: eventName },
    {
      path: pageName
        ? createURL(pagePath, `/${EXHIBITORS_PATHNAME}/${exhibitorSlug}/`)
        : "",
      label: exhibitorName,
    },
  ];

  if (pageName) {
    breadcrumbs.push({ path: "", label: pageName });
  }

  return breadcrumbs;
}
